import React, { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { generateTimelineThunk } from '../../../redux/thunks/TimelineThunks';
import { DBTimelineThunks } from '../../../redux/thunks/DBServiceThunks';
import { Timeline } from '../../../models/Timeline';
import { selectCanEditCurrentCase } from '../../../redux/selectors/knowledgebaseSelectors';

interface TimelineUpdateBannerProps {
  timeline: Timeline;
}

export default function TimelineUpdateBanner({ timeline }: TimelineUpdateBannerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Check if user can edit
  const canEdit = useAppSelector(selectCanEditCurrentCase);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await dispatch(generateTimelineThunk({
        knowledgebaseId: timeline.knowledgebaseId as string,
        caseId: timeline.caseId as string,
        title: timeline.title,
        language: 'en'
      })).unwrap();

      // Refetch the timeline to get updated status ('generating')
      await dispatch(DBTimelineThunks.find({
        knowledgebaseId: timeline.knowledgebaseId
      })).unwrap();
    } catch (error) {
      console.error('Error regenerating timeline:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await dispatch(DBTimelineThunks.patch({
        entity: {
          _id: timeline._id as string,
          needsUpdate: false
        }
      } as any)).unwrap();
    } catch (error) {
      console.error('Error dismissing banner:', error);
    }
  };

  if (!timeline.needsUpdate) {
    return null;
  }

  return (
    <Alert className="mx-4 mt-4 mb-2 border-orange-500 bg-orange-50 dark:bg-orange-950/20 relative w-auto inline-block">
      <AlertDescription className="flex flex-col gap-2 pt-5 pb-1 w-auto">
        {/* X button at top */}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          className="absolute top-2 ltr:right-2 rtl:left-2 h-6 w-6 p-0 hover:bg-orange-100 dark:hover:bg-orange-900/40"
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Alert text */}
        <p className="text-sm text-orange-900 dark:text-orange-100 pr-0">
          {t('common.timeline.updateBanner.simpleMessage')}
        </p>

        {/* Update button at bottom */}
        <div className="ltr:text-left rtl:text-right">
          <Button
            size="sm"
            onClick={handleRegenerate}
            disabled={isRegenerating || !canEdit}
            className="h-8 px-4 gap-1.5 bg-orange-600 hover:bg-orange-700 text-white"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
            {t('common.timeline.updateBanner.update')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

